// Preserve stylesheet cascade and initialization import order when maintaining this entry point.
import { MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/tiptap/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
// The Vite config aliases each dependency to its package folder, bypassing package exports, so the
// tour stylesheet is imported from where the package ships it.
import "@gfazioli/mantine-onboarding-tour/dist/styles.css";
import "@ngt-form/mantine/dist/index.css";
import "@ngt/mantine-react-table/dist/index.css";
import "./styles/app.scss";
import { Notifications } from "@mantine/notifications";
import { createRoot } from "react-dom/client";
import { SWRConfig } from "swr";
import "src/sentry";
import mantineFormOptions from "src/mantineFormOptions";
import { DatesProvider } from "@mantine/dates";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/en-au";
import { StrictMode } from "react";
import ServiceStackClientProvider from "src/components/providers/api/ServiceStackClientProvider";
import { FormAdapterProvider } from "@ngt-form/core";
import { MutativeFormAdapter } from "@ngt-form/mutative";
import AuthenticatedUserProvider from "src/components/providers/authentication/AuthenticatedUserProvider";
import { MantineFormOptionsProvider } from "@ngt-form/mantine";
import mantineTheme from "src/styles/mantineTheme";
import { mantineCssResolver } from "src/styles/mantineCssResolver";
import AppSettings from "./AppSettings";
import App from "./App";
import UserPreferencesProvider from "src/components/providers/preferences/UserPreferencesProvider";

// Install relative-time support before any component renders a date tooltip.
dayjs.extend(relativeTime);

if (import.meta.env.DEV) {
    void import("react-scan");
}

/**
 * Mounts the provider tree into the server-supplied app element.
 *
 * Preconditions: The browser document contains the app element. createRoot failures propagate.
 * Postconditions: render requests React work; providers and routes need not have mounted on return.
 * Provider order supplies service/authentication/preferences context to dependent children.
 */
const root = createRoot(document.getElementById("app") as HTMLDivElement);
root.render(
    <StrictMode>
        {/* <Sentry.ErrorBoundary fallback={<>An error has occured. Please refresh the page and try again.</>}> */}
        <SWRConfig
            value={{
                revalidateOnFocus: false,
                revalidateOnReconnect: false,
                shouldRetryOnError: false
            }}
        >
            <MantineProvider
                theme={mantineTheme}
                cssVariablesResolver={mantineCssResolver}
            >
                <DatesProvider settings={{ locale: "en-au" }}>
                    <MantineFormOptionsProvider options={mantineFormOptions}>
                        <ServiceStackClientProvider
                            baseUrl={AppSettings.client.baseUrl}
                            basePath={AppSettings.client.basePath}
                        >
                            <FormAdapterProvider adapter={MutativeFormAdapter}>
                                <AuthenticatedUserProvider
                                    configuration={{
                                        revalidateOnFocus: true,
                                        revalidateOnReconnect: true
                                    }}
                                >
                                    <UserPreferencesProvider>
                                        <Notifications
                                            position="bottom-right"
                                            limit={5}
                                            autoClose={5000}
                                        />
                                        <App />
                                    </UserPreferencesProvider>
                                </AuthenticatedUserProvider>
                            </FormAdapterProvider>
                        </ServiceStackClientProvider>
                    </MantineFormOptionsProvider>
                </DatesProvider>
            </MantineProvider>
        </SWRConfig>
        {/* </Sentry.ErrorBoundary> */}
    </StrictMode>
);
